<template>
    <v-container fluid pa-0>
        <v-row no-gutters>
            <slot name="first-column">
                <v-col>
                    <div>
                        <span>{{ title }}</span>
                        <help-button :href="helpHref"></help-button>
                    </div>
                    <slot name="below-title"></slot>
                </v-col>
            </slot>
            <slot name="second-column">
                <v-col :cols="cols.col2">
                    <div></div>
                </v-col>
            </slot>
            <slot name="third-column">
                <v-col :cols="cols.col3">
                    <slot name="query">
                        <div class="d-flex py-1 justify-end">
                            <v-text-field
                                v-if="showQuery"
                                v-model="options.query"
                                :prepend-inner-icon="icons.search"
                                label="Type here to search..."
                                single-line
                                hide-details
                                dense
                                clearable
                                data-cy="field-search"
                                @input="onQueryInput"
                            >
                            </v-text-field>
                            <span>
                                <v-tooltip top>
                                    <template v-slot:activator="{ on }">
                                        <v-btn
                                            icon
                                            color="accent"
                                            data-cy="refresh"
                                            aria-label="refresh"
                                            v-on="on"
                                            @click="onRefreshClick"
                                        >
                                            <v-icon>mdi-refresh</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Refresh data</span>
                                </v-tooltip>
                            </span>
                            <span v-if="showFilters" class="ml-1">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on }">
                                        <v-icon
                                            color="accent"
                                            class="more-data-filters"
                                            v-on="on"
                                            @click="onFiltersClick"
                                            >{{ icons.filter }}</v-icon
                                        >
                                    </template>
                                    <span>More filters...</span>
                                </v-tooltip>
                            </span>
                        </div>
                    </slot>
                    <slot name="pagination">
                        <v-data-footer
                            show-current-page
                            items-per-page-text="Rows per page:"
                            :options="footerOptions"
                            :pagination="footerPagination"
                            :items-per-page-options="itemsPerPageOptions"
                            @update:options="onFooterPaginationUpdate"
                        >
                        </v-data-footer>
                    </slot>
                    <slot name="disclaimer"></slot>
                </v-col>
            </slot>
        </v-row>
    </v-container>
</template>

<script>
import { debounce } from 'debounce';
import HelpButton from '@/components/Buttons/HelpButton';

export default {
    name: 'DataSearchBar',
    components: {
        'help-button': HelpButton
    },
    props: {
        title: {
            type: String,
            required: true
        },
        options: {
            type: Object,
            required: false,
            default: () => ({
                page: 1,
                itemsPerPage: 10,
                pageCount: 1,
                itemsLength: 1,
                query: ''
            })
        },
        showQuery: {
            type: Boolean,
            required: false,
            default: true
        },
        showFilters: {
            type: Boolean,
            required: false,
            default: false
        },
        isFiltersActive: {
            type: Boolean,
            required: false,
            default: false
        },
        cols: {
            type: Object,
            required: false,
            default: () => ({
                col1: 6,
                col2: 'auto',
                col3: 5
            })
        },
        queryDebounceTime: {
            type: Number,
            required: false,
            default: 600
        },
        helpHref: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            itemsPerPageOptions: [5, 10, 15, 20, 100, 250]
        };
    },
    computed: {
        footerOptions() {
            return {
                page: this.options.page,
                itemsPerPage: this.options.itemsPerPage
            };
        },
        footerPagination() {
            return {
                page: this.options.page,
                itemsPerPage: this.options.itemsPerPage,
                pageCount: this.options.pageCount,
                itemsLength: this.options.itemsLength,
                pageStart: (this.options.page - 1) * this.options.itemsPerPage,
                pageStop: this.options.page * this.options.itemsPerPage
            };
        }
    },
    methods: {
        // ======================== Events ==========================
        onQueryInput: debounce(function(value) {
            this.$emit('change', {
                ...this.options,
                page: 1,
                query: value || ''
            });
        }, 600),
        onFooterPaginationUpdate(pagination) {
            if (pagination) {
                this.$emit('change', {
                    ...this.options,
                    page: pagination.page,
                    itemsPerPage: pagination.itemsPerPage
                });
            }
        },
        onFiltersClick() {
            this.$emit('filter');
        },
        onRefreshClick() {
            this.$emit('refresh');
        }
    }
};
</script>

<style lang="scss" scoped>
.chart-slot {
    max-width: 310px;
    max-height: 110px;
    width: 400px;

    ::v-deep #doughnut-chart {
        max-width: 300px;
        max-height: 100px;
    }
    ::v-deep #bar-chart {
        max-width: 250px;
        max-height: 100px;
    }
    ::v-deep #horizontalbar-chart {
        max-width: 300px;
        max-height: 100px;
    }
}
::v-deep .v-data-footer__icons-before .v-btn,
::v-deep .v-data-footer__icons-after .v-btn {
    color: var(--v-accent-base);
}
::v-deep
    .v-data-footer__select
    .v-text-field
    > .v-input__control
    > .v-input__slot:before {
    border-color: var(--v-accent-base);
}
</style>
